import React, { useState, useCallback, useEffect } from 'react';
import {
  Row, Col, Card, Statistic, InputNumber, Select, Button,
  Table, Input, Space, message, Popconfirm, Empty, DatePicker,
} from 'antd';
import dayjs from 'dayjs';
import {
  SaveOutlined, DownloadOutlined,
  PlusOutlined, DeleteOutlined,
  DollarOutlined, ArrowLeftOutlined,
  PercentageOutlined, EditOutlined, TableOutlined,
} from '@ant-design/icons';
import {
  LineChart, Line, AreaChart, Area,
  XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend,
} from 'recharts';
import PageLayout from '../components/layout/PageLayout';
import api from '../libs/api';

const { Option } = Select;

const DEFAULT_INPUTS = {
  region: 'SA', postcode: '5000', installDate: dayjs().format('YYYY-MM-DD'),
  solarKw: 7, batteryKwh: 14, pvAdjustmentFactor: 0.9,
  panelPrice: 0.22, panelQty: 7000,
  solarInverterPrice: 0, solarInverterQty: 7000,
  batteryPcsPrice: 250, batteryPcsQty: 14,
  solarMaterialPrice: 0.1, solarMaterialQty: 7000,
  batteryMaterialPrice: 1400, batteryMaterialQty: 1,
  solarInstallPrice: 0.3, solarInstallQty: 7000,
  batteryInstallPrice: 1500, batteryInstallQty: 1,
  networkProtectionPrice: 12000, networkProtectionQty: 0,
  transportPrice: 0, transportQty: 1,
  designPrice: 3000, designQty: 0,
  siteWorkPrice: 3000, siteWorkQty: 0,
  pmCharge: 1000, contingency: 1000,
  stcUnitPrice: 38,
  // NSW PDRS. Only NSW projects can claim it, and it stacks with the bSTC.
  batteryInverterKw: 5, pdrsNetwork: 'ausgrid', pdrsPrice: 2.8,
  pricingModel: 'perKwh', // 'perKwh' or 'fixedMonthly'
  priceToCustomer: 0.33, fixedMonthlyPayment: 0, priceToGrid: 0,
  priceGrowthCustomerPct: 0.03, priceGrowthGridPct: 0.03,
  selfConsumptionPct: 0.33, batteryRevenuePerMwYear: 0,
  rentMonthly: 0, commMonitor: 50, serviceProvision: 50, warrantyProvision: 50,
  advanceRate: 0.8, discountRate: 0.08, interestRate: 0.04, cashflowPeriods: 120,
  retailSelfSufficient: 0.8, retailAnnualUsage: 1000, retailGridPurchase: 200,
  retailSalesPrice: 300, retailSpotPrice: 100, retailNetworkTariff: 130,
  retailEnvCost: 20, retailHedgingCost: 5,
};

// Fields stored as decimals (0.03) but displayed as percentages (3)
const PCT_FIELDS = [
  'priceGrowthCustomerPct', 'priceGrowthGridPct', 'selfConsumptionPct',
  'advanceRate', 'discountRate', 'interestRate',
];

// Convert backend decimals → display percentages (×100)
const toDisplay = (inp) => {
  const d = { ...inp };
  for (const k of PCT_FIELDS) if (d[k] != null) d[k] = Math.round(d[k] * 10000) / 100;
  return d;
};

// Convert display percentages → backend decimals (÷100)
const toDecimal = (inp) => {
  const d = { ...inp };
  for (const k of PCT_FIELDS) if (d[k] != null) d[k] = d[k] / 100;
  return d;
};

const HARDWARE_ITEMS = [
  { key: 'panel', label: 'Panel', unit: '$/W' },
  { key: 'solarInverter', label: 'Solar Inverter', unit: '$/W' },
  { key: 'batteryPcs', label: 'Battery + PCS', unit: '$/kWh' },
  { key: 'solarMaterial', label: 'Solar Material', unit: '$/W' },
  { key: 'batteryMaterial', label: 'Battery Material', unit: '$' },
  { key: 'solarInstall', label: 'Solar Install', unit: '$/W' },
  { key: 'batteryInstall', label: 'Battery Install', unit: '$' },
  { key: 'networkProtection', label: 'Network Protection', unit: '$' },
  { key: 'transport', label: 'Transport/Delivery', unit: '$' },
  { key: 'design', label: 'Design/Application/Commission', unit: '$' },
  { key: 'siteWork', label: 'Site Work', unit: '$' },
];

const fmt = (v) => {
  if (v == null) return '-';
  if (Math.abs(v) >= 1_000_000) return `$${(v / 1_000_000).toFixed(2)}M`;
  if (Math.abs(v) >= 1_000) return `$${(v / 1_000).toFixed(1)}K`;
  return `$${Number(v).toFixed(2)}`;
};
const pctFmt = (v) => v != null ? `${(v).toFixed(2)}%` : '-';

// fmt() abbreviates to K/M, which is the wrong call for a capex figure someone
// is about to quote - these keep every cent.
const money = (v) => (v == null ? '-' : `${v < 0 ? '-' : ''}$${Math.abs(v)
  .toLocaleString('en-AU', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`);
const count = (v) => (v == null ? '-' : Number(v).toLocaleString('en-AU'));

// ============= Live certificate / capex preview =============

function CertStat({ label, certs, price, value, note }) {
  const paid = certs > 0;
  return (
    <div>
      <div style={{ fontSize: 12, color: '#888' }}>{label}</div>
      <div style={{ fontSize: 18, fontWeight: 600, color: paid ? '#262B40' : '#bbb', lineHeight: 1.3 }}>
        {money(value)}
      </div>
      <div style={{ fontSize: 11, color: '#888', lineHeight: 1.4 }}>
        {paid ? `${count(certs)} @ ${money(price)}` : (note || 'nothing claimable')}
      </div>
    </div>
  );
}

/**
 * What the certificates and the net capex come to, as the inputs are typed.
 *
 * The numbers come from POST /calculate - the same path the save and the Excel
 * download run through - rather than being worked out again here. A second
 * implementation in the browser is exactly how the page and the workbook would
 * start disagreeing, which is the bug this model has already had twice.
 */
function LivePreview({ data, hint, isNsw }) {
  const capex = data?.capex;
  const info = data?.stcInfo;
  // Flex rather than spans: the PDRS column only exists for NSW, and a fixed
  // span count leaves a ragged gap on whichever branch it was not sized for.
  const cell = { flex: '1 1 0', style: { minWidth: 0 } };

  return (
    <Card
      size="small"
      style={{ marginBottom: 16, position: 'sticky', top: 8, zIndex: 9,
               borderColor: '#54A2CC', boxShadow: '0 2px 8px rgba(38,43,64,0.08)' }}
      styles={{ body: { padding: '12px 16px' } }}
    >
      {!capex ? (
        <div style={{ color: '#888', fontSize: 13 }}>{hint || 'Calculating...'}</div>
      ) : (
        <Row gutter={16} align="middle" wrap={false}>
          <Col {...cell}>
            <CertStat
              label="Solar STC" certs={capex.solarStcCount} price={capex.stcUnitPrice}
              value={capex.solarStcValue}
              note={info?.solarStcDetail ? 'nothing claimable' : 'check the postcode'}
            />
          </Col>
          <Col {...cell}>
            <CertStat
              label="Battery STC" certs={capex.bstcCount} price={capex.stcUnitPrice}
              value={capex.bstcValue} note={info?.bstcDetail?.reason}
            />
          </Col>
          {isNsw && (
            <Col {...cell}>
              <CertStat
                label="NSW PDRS" certs={capex.pdrsCount} price={capex.pdrsPrice}
                value={capex.pdrsValue} note={info?.pdrsDetail?.reason}
              />
            </Col>
          )}
          <Col {...cell}>
            <div style={{ fontSize: 12, color: '#888' }}>Hardware + PM + Cont.</div>
            <div style={{ fontSize: 18, fontWeight: 600, color: '#262B40', lineHeight: 1.3 }}>
              {money(capex.systemCost + capex.pmCharge + capex.contingency)}
            </div>
            <div style={{ fontSize: 11, color: '#888', lineHeight: 1.4 }}>
              {money(capex.systemCost)} hardware
            </div>
          </Col>
          <Col flex="0 0 200px" style={{ borderLeft: '1px solid #eee', paddingLeft: 16 }}>
            <div style={{ fontSize: 12, color: '#888' }}>Net CAPEX</div>
            <div style={{ fontSize: 24, fontWeight: 700, color: '#54A2CC', lineHeight: 1.2 }}>
              {money(capex.totalCapex)}
            </div>
            <div style={{ fontSize: 11, color: '#888', lineHeight: 1.4 }}>
              after {money(Math.abs(capex.totalIncentives))} of incentives
            </div>
          </Col>
        </Row>
      )}
    </Card>
  );
}

// ============= Results Panel (reused in list view) =============
function ResultsPanel({ results }) {
  if (!results) return <Empty description="No results — save a scenario to see results" style={{ padding: 40 }} />;

  const capex = results.capex || {};
  const opex = results.opex || {};
  const revenue = results.revenue || {};
  const npvIrr = results.npvIrr || {};
  const lcoe = results.lcoe || {};
  const stcInfo = results.stcInfo || {};
  const cashflows = results.cashflows || [];

  // CAPEX breakdown table data — show kW/kWh from inputs that drove the STC counts
  const sd = stcInfo.solarStcDetail;
  const bd = stcInfo.bstcDetail;
  const solarStcLabel = sd
    ? `Solar STC (${capex.solarStcCount} STCs = ${stcInfo.solarKw}kW × ${sd.deemingPeriod}yr × ${sd.zoneRating})`
    : capex.solarStcCount != null ? `Solar STC (${capex.solarStcCount} STCs)` : 'Solar STC';
  const bstcLabel = bd && bd.stcs > 0
    ? (bd.eligible === false
        ? `Battery STC — not claimable: ${bd.reason}`
        : `Battery STC (${capex.bstcCount} bSTCs — ${stcInfo.batteryKwh}kWh, ${bd.period}${bd.tapered ? ' tapered' : ''})`)
    : capex.bstcCount != null ? `Battery STC (${capex.bstcCount} bSTCs)` : 'Battery STC';
  // NSW PDRS. The reason string carries why a project is ineligible, so a zero
  // never looks like a calculation that silently failed.
  const pd = stcInfo.pdrsDetail;
  const pdrsLabel = pd && pd.eligible
    ? `NSW PDRS (${capex.pdrsCount} PRCs — ${pd.activity}, ${pd.cappedKwh}kWh capped, DSC ${pd.demandShiftingComponent}kW, ${pd.networkLabel})`
    : `NSW PDRS${pd && pd.reason ? ` — not claimable: ${pd.reason}` : ''}`;
  const capexBreakdown = [
    { key: 'system', label: 'System Cost (Hardware)', amount: capex.systemCost },
    { key: 'pm', label: 'PM Charge', amount: capex.pmCharge },
    { key: 'contingency', label: 'Contingency', amount: capex.contingency },
    { key: 'solarStc', label: solarStcLabel, amount: capex.solarStcValue },
    { key: 'bstc', label: bstcLabel, amount: capex.bstcValue },
    { key: 'pdrs', label: pdrsLabel, amount: capex.pdrsValue },
    { key: 'total', label: 'Net CAPEX', amount: capex.totalCapex, bold: true },
  ];

  return (
    <>
      <Row gutter={[16, 16]}>
        <Col xs={12} md={6}>
          <Card><Statistic title="Net CAPEX" value={capex.totalCapex} formatter={fmt} prefix={<DollarOutlined style={{ color: '#54A2CC' }} />} /></Card>
        </Col>
        <Col xs={12} md={6}>
          <Card><Statistic title="First Year Revenue" value={revenue.firstYearRevenue} formatter={fmt} prefix={<DollarOutlined style={{ color: '#05A677' }} />} valueStyle={{ color: '#05A677' }} /></Card>
        </Col>
        <Col xs={12} md={6}>
          <Card><Statistic title="Annual OPEX" value={opex.totalOpex} formatter={fmt} prefix={<DollarOutlined style={{ color: '#FA5252' }} />} valueStyle={{ color: '#FA5252' }} /></Card>
        </Col>
        <Col xs={12} md={6}>
          <Card><Statistic title="Project IRR" value={npvIrr.projectIrr} formatter={pctFmt} prefix={<PercentageOutlined style={{ color: '#f5b759' }} />} valueStyle={{ color: '#f5b759' }} /></Card>
        </Col>
      </Row>

      <Row gutter={[16, 16]} style={{ marginTop: 16 }}>
        <Col xs={8} md={4}><Card size="small"><Statistic title="NPV (10yr)" value={npvIrr.npv10} formatter={fmt} valueStyle={{ fontSize: 16 }} /></Card></Col>
        <Col xs={8} md={4}><Card size="small"><Statistic title="Net Project IRR" value={npvIrr.netProjectIrr} formatter={pctFmt} valueStyle={{ fontSize: 16 }} /></Card></Col>
        <Col xs={8} md={4}><Card size="small"><Statistic title="Equity NPV" value={npvIrr.equityNpv} formatter={fmt} valueStyle={{ fontSize: 16 }} /></Card></Col>
        <Col xs={8} md={4}><Card size="small"><Statistic title="Equity IRR" value={npvIrr.equityIrr} formatter={pctFmt} valueStyle={{ fontSize: 16 }} /></Card></Col>
        <Col xs={8} md={4}><Card size="small"><Statistic title="PV LCOE" value={lcoe.pvLcoe10} precision={4} prefix="$" suffix="/kWh" valueStyle={{ fontSize: 16 }} /></Card></Col>
        <Col xs={8} md={4}><Card size="small"><Statistic title="PV+BESS LCOE" value={lcoe.pvBessLcoe10} precision={4} prefix="$" suffix="/kWh" valueStyle={{ fontSize: 16 }} /></Card></Col>
      </Row>
      <Row gutter={[16, 16]} style={{ marginTop: 16 }}>
        <Col xs={8} md={4}><Card size="small"><Statistic title="PV Capex" value={lcoe.pvCapex} formatter={fmt} valueStyle={{ fontSize: 16 }} /></Card></Col>
        <Col xs={8} md={4}><Card size="small"><Statistic title="PV+BESS Capex" value={lcoe.pvBessCapex} formatter={fmt} valueStyle={{ fontSize: 16 }} /></Card></Col>
        <Col xs={8} md={4}><Card size="small"><Statistic title="Total Gen (10yr)" value={lcoe.totalGenMwh} precision={3} suffix=" MWh" valueStyle={{ fontSize: 16 }} /></Card></Col>
        <Col xs={8} md={4}><Card size="small"><Statistic title="Disc. Gen (10yr)" value={lcoe.discountedGenMwh} precision={3} suffix=" MWh" valueStyle={{ fontSize: 16 }} /></Card></Col>
      </Row>

      {/* CAPEX Breakdown */}
      <Card title="CAPEX Breakdown" size="small" style={{ marginTop: 16 }}>
        <Table
          dataSource={capexBreakdown}
          rowKey="key"
          size="small"
          pagination={false}
          columns={[
            { title: 'Item', dataIndex: 'label', render: (v, r) => r.bold ? <strong>{v}</strong> : v },
            { title: 'Amount', dataIndex: 'amount', width: 180, align: 'right',
              render: (v, r) => {
                if (v == null) return '-';
                const formatted = `$${Number(v).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
                return r.bold ? <strong>{formatted}</strong> : <span style={{ color: v < 0 ? '#05A677' : undefined }}>{formatted}</span>;
              },
            },
          ]}
        />
      </Card>

      <Row gutter={[16, 16]} style={{ marginTop: 16 }}>
        <Col xs={24} lg={12}>
          <Card title="Cumulative Cashflow" size="small">
            <ResponsiveContainer width="100%" height={300}>
              <LineChart data={cashflows} margin={{ top: 5, right: 20, bottom: 5, left: 10 }}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="month" fontSize={10} label={{ value: 'Month', position: 'insideBottom', offset: -5 }} />
                <YAxis tickFormatter={fmt} fontSize={11} />
                <Tooltip formatter={(v) => [fmt(v)]} />
                <Line type="monotone" dataKey="cumulativeCf" stroke="#54A2CC" strokeWidth={2} dot={false} name="Cumulative CF" />
              </LineChart>
            </ResponsiveContainer>
          </Card>
        </Col>
        <Col xs={24} lg={12}>
          <Card title="Monthly Revenue Breakdown" size="small">
            <ResponsiveContainer width="100%" height={300}>
              <AreaChart data={cashflows.filter((_, i) => i % 3 === 0)} margin={{ top: 5, right: 20, bottom: 5, left: 10 }}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="month" fontSize={10} />
                <YAxis tickFormatter={fmt} fontSize={11} />
                <Tooltip formatter={(v) => [fmt(v)]} />
                <Area type="monotone" dataKey="custRev" stackId="1" fill="#05A677" stroke="#05A677" name="Customer Revenue" />
                <Area type="monotone" dataKey="gridRev" stackId="1" fill="#54A2CC" stroke="#54A2CC" name="Grid Revenue" />
                <Area type="monotone" dataKey="battRev" stackId="1" fill="#f5b759" stroke="#f5b759" name="Battery Revenue" />
                <Legend />
              </AreaChart>
            </ResponsiveContainer>
          </Card>
        </Col>
      </Row>
    </>
  );
}

// ============= Cashflow Table View =============
function CashflowView({ scenario, onBack }) {
  const cashflows = scenario?.results?.cashflows || [];
  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'center', marginBottom: 16, gap: 12 }}>
        <Button icon={<ArrowLeftOutlined />} onClick={onBack}>Back to Scenarios</Button>
        <span style={{ fontSize: 16, fontWeight: 600 }}>{scenario?.name} — Cashflow Table</span>
      </div>
      <Card size="small">
        <Table
          dataSource={cashflows}
          rowKey="month"
          size="small"
          pagination={{ defaultPageSize: 24, showSizeChanger: true, pageSizeOptions: [12, 24, 60, 120] }}
          scroll={{ x: 1300 }}
          columns={[
            { title: '#', dataIndex: 'month', width: 50, fixed: 'left' },
            { title: 'Month', dataIndex: 'calMonth', width: 100, fixed: 'left' },
            { title: 'Gen (MWh)', dataIndex: 'genMwh', width: 100, align: 'right' },
            { title: 'Customer $', dataIndex: 'custRev', width: 110, align: 'right', render: (v) => `$${v.toFixed(2)}` },
            { title: 'Grid $', dataIndex: 'gridRev', width: 100, align: 'right', render: (v) => `$${v.toFixed(2)}` },
            { title: 'Battery $', dataIndex: 'battRev', width: 100, align: 'right', render: (v) => `$${v.toFixed(2)}` },
            { title: 'Total Rev', dataIndex: 'totalRev', width: 110, align: 'right', render: (v) => `$${v.toFixed(2)}` },
            { title: 'OPEX', dataIndex: 'opex', width: 90, align: 'right', render: (v) => `$${v.toFixed(2)}` },
            { title: 'Interest', dataIndex: 'interest', width: 90, align: 'right', render: (v) => `$${v.toFixed(2)}` },
            { title: 'Principal', dataIndex: 'principal', width: 90, align: 'right', render: (v) => `$${v.toFixed(2)}` },
            { title: 'Net CF', dataIndex: 'netCf', width: 110, align: 'right', render: (v) => <span style={{ color: v >= 0 ? '#05A677' : '#FA5252' }}>${v.toFixed(2)}</span> },
            { title: 'Cumulative', dataIndex: 'cumulativeCf', width: 120, align: 'right', render: (v) => <span style={{ color: v >= 0 ? '#05A677' : '#FA5252' }}>{fmt(v)}</span> },
          ]}
        />
      </Card>
    </div>
  );
}

// ============= Inputs View =============
function InputsView({ scenario, onBack, onSaved }) {
  const isNew = !scenario.id;
  // Convert decimals → display percentages for editing, and sync auto-derived quantities
  const [inputs, setInputs] = useState(() => {
    const merged = toDisplay({ ...DEFAULT_INPUTS, ...(scenario.inputs || {}) });
    const watts = (merged.solarKw || 0) * 1000;
    merged.panelQty = watts;
    merged.solarInverterQty = watts;
    merged.solarMaterialQty = watts;
    merged.solarInstallQty = watts;
    merged.batteryPcsQty = merged.batteryKwh || 0;
    return merged;
  });
  const [name, setName] = useState(scenario.name || '');
  const [description, setDescription] = useState(scenario.description || '');
  const [saving, setSaving] = useState(false);

  // PDRS is a NSW scheme. Region drives the backend calculation, so the inputs
  // follow region rather than postcode to avoid the two disagreeing.
  const isNsw = String(inputs.region || '').toUpperCase() === 'NSW';

  const updateInput = (key, value) => setInputs(prev => {
    const next = { ...prev, [key]: value };
    // Auto-sync CAPEX quantities when System Specs change
    if (key === 'solarKw') {
      const watts = (value || 0) * 1000;
      next.panelQty = watts;
      next.solarInverterQty = watts;
      next.solarMaterialQty = watts;
      next.solarInstallQty = watts;
    } else if (key === 'batteryKwh') {
      next.batteryPcsQty = value || 0;
    }
    return next;
  });

  const systemCost = HARDWARE_ITEMS.reduce(
    (s, item) => s + (inputs[item.key + 'Price'] || 0) * (inputs[item.key + 'Qty'] || 0), 0
  );

  // Live preview of the certificates and the net capex, recalculated server
  // side as the inputs change. Debounced so a held arrow key does not fire a
  // request per keystroke, and guarded so a slow early response cannot land
  // after a newer one and show a stale number.
  const [preview, setPreview] = useState(null);
  const [previewHint, setPreviewHint] = useState(null);

  useEffect(() => {
    if (!inputs.region || (!inputs.solarKw && !inputs.batteryKwh)) {
      setPreview(null);
      setPreviewHint('Enter Region and a system size to see certificates and net CAPEX');
      return undefined;
    }
    let cancelled = false;
    const timer = setTimeout(async () => {
      try {
        const res = await api.post('/api/project-finance/calculate', toDecimal(inputs));
        if (cancelled) return;
        setPreview(res.data?.data || null);
        setPreviewHint(null);
      } catch (err) {
        if (cancelled) return;
        setPreview(null);
        setPreviewHint(err.response?.data?.message || 'Preview unavailable');
      }
    }, 400);
    return () => { cancelled = true; clearTimeout(timer); };
  }, [inputs]);

  const handleSave = async () => {
    if (!name.trim()) { message.warning('Please enter a scenario name'); return; }
    if (!inputs.region || !inputs.solarKw) { message.warning('Region and Solar kW are required'); return; }
    if (!inputs.postcode || !/^\d{4}$/.test(inputs.postcode)) { message.warning('Please enter a valid 4-digit Australian postcode'); return; }
    setSaving(true);
    try {
      // Convert display percentages → decimals for backend
      const apiInputs = toDecimal(inputs);
      if (isNew) {
        await api.post('/api/project-finance/scenarios', { name, description, inputs: apiInputs });
      } else {
        await api.put(`/api/project-finance/scenarios/${scenario.id}`, { name, description, inputs: apiInputs });
      }
      message.success(isNew ? 'Scenario created' : 'Scenario updated');
      onSaved();
    } catch (err) {
      message.error(err.response?.data?.message || 'Save failed');
    }
    setSaving(false);
  };

  const isFixedMonthly = inputs.pricingModel === 'fixedMonthly';

  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'center', marginBottom: 16, gap: 12 }}>
        <Button icon={<ArrowLeftOutlined />} onClick={onBack}>Back to Scenarios</Button>
        <span style={{ fontSize: 16, fontWeight: 600 }}>{isNew ? 'New Scenario' : `Edit: ${scenario.name}`}</span>
      </div>

      {/* Scenario Name & Description */}
      <Card size="small" style={{ marginBottom: 16 }}>
        <Row gutter={16}>
          <Col span={8}>
            <div style={{ marginBottom: 4, fontWeight: 500 }}>Scenario Name *</div>
            <Input value={name} onChange={(e) => setName(e.target.value)} placeholder="e.g. 7kW Solar + 14kWh Battery SA" />
          </Col>
          <Col span={16}>
            <div style={{ marginBottom: 4, fontWeight: 500 }}>Description</div>
            <Input value={description} onChange={(e) => setDescription(e.target.value)} placeholder="Optional notes" />
          </Col>
        </Row>
      </Card>

      {/* System Specs */}
      <Card title="System Specifications" size="small" style={{ marginBottom: 16 }}>
        <Row gutter={16}>
          <Col span={4}>
            <div style={{ marginBottom: 8, fontWeight: 500 }}>Region</div>
            <Select value={inputs.region} onChange={(v) => updateInput('region', v)} style={{ width: '100%' }} size="small">
              <Option value="SA">SA</Option><Option value="NSW">NSW</Option>
              <Option value="QLD">QLD</Option><Option value="VIC">VIC</Option>
            </Select>
          </Col>
          <Col span={4}>
            <div style={{ marginBottom: 8, fontWeight: 500 }}>Postcode *</div>
            <Input value={inputs.postcode} onChange={(e) => updateInput('postcode', e.target.value)} placeholder="e.g. 5000" maxLength={4} style={{ width: '100%' }} size="small" />
          </Col>
          <Col span={4}>
            <div style={{ marginBottom: 8, fontWeight: 500 }}>Install Date</div>
            <DatePicker
              value={inputs.installDate ? dayjs(inputs.installDate) : null}
              onChange={(d) => updateInput('installDate', d ? d.format('YYYY-MM-DD') : null)}
              style={{ width: '100%' }} size="small"
              format="DD/MM/YYYY"
            />
          </Col>
          <Col span={4}>
            <div style={{ marginBottom: 8, fontWeight: 500 }}>Solar (kW)</div>
            <InputNumber value={inputs.solarKw} onChange={(v) => updateInput('solarKw', v)} min={0} style={{ width: '100%' }} size="small" />
          </Col>
          <Col span={4}>
            <div style={{ marginBottom: 8, fontWeight: 500 }}>Battery (kWh)</div>
            <InputNumber value={inputs.batteryKwh} onChange={(v) => updateInput('batteryKwh', v)} min={0} style={{ width: '100%' }} size="small" />
          </Col>
          <Col span={4}>
            <div style={{ marginBottom: 8, fontWeight: 500 }}>Batt Inverter (kW)</div>
            <InputNumber value={inputs.batteryInverterKw} onChange={(v) => updateInput('batteryInverterKw', v)} min={0} style={{ width: '100%' }} size="small" />
          </Col>
          <Col span={4}>
            <div style={{ marginBottom: 8, fontWeight: 500 }}>PV Adjustment</div>
            <InputNumber value={inputs.pvAdjustmentFactor} onChange={(v) => updateInput('pvAdjustmentFactor', v)} min={0} max={2} step={0.1} style={{ width: '100%' }} size="small" />
          </Col>
        </Row>
      </Card>

      <LivePreview data={preview} hint={previewHint} isNsw={isNsw} />

      {/* CAPEX Hardware */}
      <Card title="CAPEX — Hardware" size="small" style={{ marginBottom: 16 }}>
        <Table
          dataSource={HARDWARE_ITEMS.map(item => ({
            ...item,
            price: inputs[item.key + 'Price'] || 0,
            qty: inputs[item.key + 'Qty'] || 0,
            total: (inputs[item.key + 'Price'] || 0) * (inputs[item.key + 'Qty'] || 0),
          }))}
          rowKey="key"
          size="small"
          pagination={false}
          columns={[
            { title: 'Component', dataIndex: 'label', width: 220 },
            { title: 'Unit', dataIndex: 'unit', width: 80 },
            {
              title: 'Unit Price', dataIndex: 'price', width: 130, align: 'right',
              render: (_, record) => (
                <InputNumber value={record.price} onChange={(v) => updateInput(record.key + 'Price', v)} min={0} size="small" style={{ width: 110 }} />
              ),
            },
            {
              title: 'Quantity', dataIndex: 'qty', width: 130, align: 'right',
              render: (_, record) => {
                // Auto-synced from System Specs — read-only
                const autoSync = ['panel', 'solarInverter', 'solarMaterial', 'solarInstall', 'batteryPcs'];
                const isAuto = autoSync.includes(record.key);
                return <InputNumber value={record.qty} onChange={(v) => updateInput(record.key + 'Qty', v)} min={0} size="small" style={{ width: 110 }} disabled={isAuto} />;
              },
            },
            { title: 'Total', dataIndex: 'total', width: 120, align: 'right', render: (v) => `$${v.toLocaleString()}` },
          ]}
          summary={() => (
            <Table.Summary.Row>
              <Table.Summary.Cell colSpan={4}><strong>System Cost</strong></Table.Summary.Cell>
              <Table.Summary.Cell align="right"><strong>${systemCost.toLocaleString()}</strong></Table.Summary.Cell>
            </Table.Summary.Row>
          )}
        />
      </Card>

      {/* CAPEX Adjustments + OPEX */}
      <Row gutter={16}>
        <Col span={12}>
          <Card title="CAPEX Adjustments" size="small" style={{ marginBottom: 16 }}>
            <Row gutter={[16, 8]}>
              <Col span={12}><div>PM Charge</div><InputNumber value={inputs.pmCharge} onChange={(v) => updateInput('pmCharge', v)} min={0} prefix="$" style={{ width: '100%' }} size="small" /></Col>
              <Col span={12}><div>Contingency</div><InputNumber value={inputs.contingency} onChange={(v) => updateInput('contingency', v)} min={0} prefix="$" style={{ width: '100%' }} size="small" /></Col>
              <Col span={isNsw ? 12 : 24}><div>STC Unit Price ($/STC)</div><InputNumber value={inputs.stcUnitPrice} onChange={(v) => updateInput('stcUnitPrice', v)} min={0} step={0.01} prefix="$" style={{ width: isNsw ? '100%' : '50%' }} size="small" /></Col>
              {/* PDRS is a NSW scheme, so its inputs only appear for NSW projects */}
              {isNsw && (
                <>
                  <Col span={12}><div>PDRS Price ($/PRC)</div><InputNumber value={inputs.pdrsPrice} onChange={(v) => updateInput('pdrsPrice', v)} min={0} step={0.01} prefix="$" style={{ width: '100%' }} size="small" /></Col>
                  <Col span={12}>
                    <div>NSW Network (loss factor)</div>
                    <Select value={inputs.pdrsNetwork} onChange={(v) => updateInput('pdrsNetwork', v)} style={{ width: '100%' }} size="small">
                      <Option value="ausgrid">Ausgrid (1.04)</Option>
                      <Option value="endeavour">Endeavour Energy (1.05)</Option>
                      <Option value="essential">Essential Energy (1.05)</Option>
                    </Select>
                  </Col>
                </>
              )}
            </Row>
            <div style={{ marginTop: 8, fontSize: 12, color: '#888' }}>
              STC &amp; bSTC counts are auto-calculated from postcode, system size and battery capacity.
              {isNsw
                ? ' NSW PDRS is calculated from usable battery capacity, battery inverter output and network, and stacks on top of the bSTC.'
                : ' NSW PDRS does not apply outside NSW, so its inputs are hidden.'}
            </div>
          </Card>
        </Col>
        <Col span={12}>
          <Card title="OPEX (Annual)" size="small" style={{ marginBottom: 16 }}>
            <Row gutter={[16, 8]}>
              <Col span={12}><div>Rent (Monthly)</div><InputNumber value={inputs.rentMonthly} onChange={(v) => updateInput('rentMonthly', v)} min={0} prefix="$" style={{ width: '100%' }} size="small" /></Col>
              <Col span={12}><div>Comm & Monitor</div><InputNumber value={inputs.commMonitor} onChange={(v) => updateInput('commMonitor', v)} min={0} prefix="$" style={{ width: '100%' }} size="small" /></Col>
              <Col span={12}><div>Service Provision</div><InputNumber value={inputs.serviceProvision} onChange={(v) => updateInput('serviceProvision', v)} min={0} prefix="$" style={{ width: '100%' }} size="small" /></Col>
              <Col span={12}><div>Warranty Provision</div><InputNumber value={inputs.warrantyProvision} onChange={(v) => updateInput('warrantyProvision', v)} min={0} prefix="$" style={{ width: '100%' }} size="small" /></Col>
            </Row>
          </Card>
        </Col>
      </Row>

      {/* Revenue + Cashflow Config */}
      <Row gutter={16}>
        <Col span={12}>
          <Card title="Revenue Assumptions" size="small" style={{ marginBottom: 16 }}>
            <Row gutter={[16, 8]}>
              <Col span={24}>
                <div>Pricing Model</div>
                <Select value={inputs.pricingModel || 'perKwh'} onChange={(v) => updateInput('pricingModel', v)} style={{ width: '100%' }} size="small">
                  <Option value="perKwh">Unit Price / kWh</Option>
                  <Option value="fixedMonthly">Fixed Monthly Payment</Option>
                </Select>
              </Col>
              {isFixedMonthly ? (
                <Col span={12}><div>Monthly Payment</div><InputNumber value={inputs.fixedMonthlyPayment} onChange={(v) => updateInput('fixedMonthlyPayment', v)} min={0} prefix="$" style={{ width: '100%' }} size="small" /></Col>
              ) : (
                <Col span={12}><div>Price to Customer ($/kWh)</div><InputNumber value={inputs.priceToCustomer} onChange={(v) => updateInput('priceToCustomer', v)} min={0} step={0.01} style={{ width: '100%' }} size="small" /></Col>
              )}
              <Col span={12}><div>Price Growth</div><InputNumber value={inputs.priceGrowthCustomerPct} onChange={(v) => updateInput('priceGrowthCustomerPct', v)} min={0} max={100} step={1} addonAfter="%" style={{ width: '100%' }} size="small" /></Col>
              <Col span={12}><div>Price to Grid ($/kWh)</div><InputNumber value={inputs.priceToGrid} onChange={(v) => updateInput('priceToGrid', v)} min={0} step={0.01} style={{ width: '100%' }} size="small" /></Col>
              <Col span={12}><div>Grid Price Growth</div><InputNumber value={inputs.priceGrowthGridPct} onChange={(v) => updateInput('priceGrowthGridPct', v)} min={0} max={100} step={1} addonAfter="%" style={{ width: '100%' }} size="small" /></Col>
              <Col span={12}><div>Self-Consumption</div><InputNumber value={inputs.selfConsumptionPct} onChange={(v) => updateInput('selfConsumptionPct', v)} min={0} max={100} step={1} addonAfter="%" style={{ width: '100%' }} size="small" /></Col>
              <Col span={12}><div>Battery Rev ($/MW/Year)</div><InputNumber value={inputs.batteryRevenuePerMwYear} onChange={(v) => updateInput('batteryRevenuePerMwYear', v)} min={0} style={{ width: '100%' }} size="small" /></Col>
            </Row>
          </Card>
        </Col>
        <Col span={12}>
          <Card title="Cashflow Configuration" size="small" style={{ marginBottom: 16 }}>
            <Row gutter={[16, 8]}>
              <Col span={12}><div>Discount Rate</div><InputNumber value={inputs.discountRate} onChange={(v) => updateInput('discountRate', v)} min={0} max={100} step={1} addonAfter="%" style={{ width: '100%' }} size="small" /></Col>
              <Col span={12}><div>Interest Rate</div><InputNumber value={inputs.interestRate} onChange={(v) => updateInput('interestRate', v)} min={0} max={100} step={1} addonAfter="%" style={{ width: '100%' }} size="small" /></Col>
              <Col span={12}><div>Advance Rate</div><InputNumber value={inputs.advanceRate} onChange={(v) => updateInput('advanceRate', v)} min={0} max={100} step={1} addonAfter="%" style={{ width: '100%' }} size="small" /></Col>
              <Col span={12}><div>Period (months)</div><InputNumber value={inputs.cashflowPeriods} onChange={(v) => updateInput('cashflowPeriods', v)} min={12} max={360} step={12} style={{ width: '100%' }} size="small" /></Col>
            </Row>
          </Card>
        </Col>
      </Row>

      {/* Save Button */}
      <div style={{ textAlign: 'center', marginTop: 8, marginBottom: 16 }}>
        <Button type="primary" size="large" icon={<SaveOutlined />} onClick={handleSave} loading={saving}>
          Save & Calculate
        </Button>
      </div>
    </div>
  );
}

// ============= Main Component =============
export default function ProjectFinance() {
  // view: 'list' | 'inputs' | 'cashflow'
  const [view, setView] = useState('list');
  const [scenarios, setScenarios] = useState([]);
  const [scenariosLoading, setScenariosLoading] = useState(false);
  const [selectedId, setSelectedId] = useState(null);
  // Full scenario data for editing or cashflow viewing
  const [editingScenario, setEditingScenario] = useState(null);

  const loadScenarios = useCallback(async () => {
    setScenariosLoading(true);
    try {
      const res = await api.get('/api/project-finance/scenarios');
      setScenarios(res.data.data || []);
    } catch { /* ignore */ }
    setScenariosLoading(false);
  }, []);

  useEffect(() => { loadScenarios(); }, [loadScenarios]);

  // Load full scenario detail (inputs + results)
  const loadFullScenario = async (id) => {
    try {
      const res = await api.get(`/api/project-finance/scenarios/${id}`);
      return res.data.data;
    } catch {
      message.error('Failed to load scenario');
      return null;
    }
  };

  const handleSelectScenario = async (id) => {
    setSelectedId(id);
    // Preload full data for results panel
    const full = await loadFullScenario(id);
    if (full) {
      setEditingScenario(full);
    }
  };

  const handleEditInputs = async (id) => {
    const full = await loadFullScenario(id);
    if (full) {
      setEditingScenario(full);
      setView('inputs');
    }
  };

  const handleViewCashflow = async (id) => {
    const full = await loadFullScenario(id);
    if (full) {
      setEditingScenario(full);
      setView('cashflow');
    }
  };

  const handleNewScenario = () => {
    setEditingScenario({ id: null, name: '', description: '', inputs: { ...DEFAULT_INPUTS }, results: null });
    setView('inputs');
  };

  const handleSaved = () => {
    setView('list');
    setSelectedId(null);
    setEditingScenario(null);
    loadScenarios();
  };

  const handleBack = () => {
    setView('list');
  };

  const deleteScenario = async (id) => {
    try {
      await api.delete(`/api/project-finance/scenarios/${id}`);
      if (selectedId === id) {
        setSelectedId(null);
        setEditingScenario(null);
      }
      message.success('Scenario deleted');
      loadScenarios();
    } catch {
      message.error('Failed to delete scenario');
    }
  };

  const downloadScenario = (id) => {
    const token = localStorage.getItem('token');
    window.open(`/api/project-finance/scenarios/${id}/download?token=${token}`, '_blank');
  };

  // Selected scenario's results for the results panel
  const selectedResults = (selectedId && editingScenario?.id === selectedId) ? editingScenario?.results : null;
  const selectedName = (selectedId && editingScenario?.id === selectedId) ? editingScenario?.name : null;

  // ======= LIST VIEW =======
  if (view === 'list') {
    return (
      <PageLayout>
        <div className="page-header">
          <h1>Project Finance Model</h1>
          <p>Solar / Battery project finance calculator with scenario management</p>
        </div>

        <div style={{ marginBottom: 16 }}>
          <Space>
            <Button type="primary" icon={<PlusOutlined />} onClick={handleNewScenario}>
              New Scenario
            </Button>
          </Space>
        </div>

        <Card size="small" style={{ marginBottom: 16 }}>
          <Table
            dataSource={scenarios}
            rowKey="id"
            size="small"
            loading={scenariosLoading}
            pagination={{ pageSize: 10 }}
            onRow={(record) => ({
              onClick: () => handleSelectScenario(record.id),
              style: { cursor: 'pointer', background: selectedId === record.id ? '#e6f4ff' : undefined },
            })}
            columns={[
              { title: 'Name', dataIndex: 'name', width: 200, render: (v, r) => <span style={{ fontWeight: selectedId === r.id ? 600 : 400 }}>{v}</span> },
              { title: 'Description', dataIndex: 'description', width: 250, ellipsis: true },
              { title: 'Created By', width: 130, render: (_, r) => `${r.first_name || ''} ${r.last_name || ''}`.trim() },
              { title: 'Updated', dataIndex: 'updated_at', width: 160, render: (v) => v ? new Date(v).toLocaleString() : '-' },
              {
                title: 'Actions', width: 280,
                render: (_, record) => (
                  <Space onClick={(e) => e.stopPropagation()}>
                    <Button size="small" icon={<EditOutlined />} onClick={() => handleEditInputs(record.id)}>Inputs</Button>
                    <Button size="small" icon={<TableOutlined />} onClick={() => handleViewCashflow(record.id)}>Cashflow</Button>
                    <Button size="small" icon={<DownloadOutlined />} onClick={() => downloadScenario(record.id)}>Excel</Button>
                    <Popconfirm title="Delete this scenario?" onConfirm={() => deleteScenario(record.id)}>
                      <Button size="small" danger icon={<DeleteOutlined />} />
                    </Popconfirm>
                  </Space>
                ),
              },
            ]}
          />
        </Card>

        {/* Results Panel for selected scenario */}
        {selectedId && (
          <Card title={`Results: ${selectedName || '...'}`} size="small">
            <ResultsPanel results={selectedResults} />
          </Card>
        )}
      </PageLayout>
    );
  }

  // ======= INPUTS VIEW =======
  if (view === 'inputs' && editingScenario) {
    return (
      <PageLayout>
        <div className="page-header">
          <h1>Project Finance Model</h1>
        </div>
        <InputsView scenario={editingScenario} onBack={handleBack} onSaved={handleSaved} />
      </PageLayout>
    );
  }

  // ======= CASHFLOW VIEW =======
  if (view === 'cashflow' && editingScenario) {
    return (
      <PageLayout>
        <div className="page-header">
          <h1>Project Finance Model</h1>
        </div>
        <CashflowView scenario={editingScenario} onBack={handleBack} />
      </PageLayout>
    );
  }

  return null;
}
